/* eslint-disable max-lines -- Fixme when deprecated objectless. */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  InjectionToken,
  Injector,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { pick } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { mapAndCacheArrayElements } from '@sama/rxjs-core';
import { keys } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ShapeState2D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MouseEventService } from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { expandShapeGroups } from '../../../cross-team-shared/misc-utils';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { ShapeOrGroupOrderItem } from '../../../cross-team-shared/state/shape-group-state';
import { Dimensions } from '../../../cross-team-shared/types/misc';
import { subtractPoints } from '../../../shared/misc-utilities/point-utils';
import { ShapeGroupOrderState } from '../../../team-apis/content/content-connector';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { MouseCoordinateService } from '../mouse-coordinate.service';
import { Selection } from '../state/abstract-sensor-content-state-2d';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { PanningService } from '../utils/panning.service';
import { VectorRendererCssPropertyService } from './vector-renderer-css-property.service';
import { VectorRendererCursorCssPropertyService } from './vector-renderer-cursor-css-property.service';
import { applyFilters } from './vector-renderer.utils';

export const MISSING_SHAPE_MARKER_SVG_SIZE = 24;

export const scaleInjectionToken = new InjectionToken<Observable<number>>(
  'scale',
);

@Component({
  selector: 'h3-vector-renderer',
  templateUrl: './vector-renderer.component.html',
  styleUrls: ['./vector-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: scaleInjectionToken,
      deps: [VectorRendererComponent],
      useFactory: (self: VectorRendererComponent) => self.scale$,
    },
  ],
  standalone: false,
})
export class VectorRendererComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @Input() isOverlay = false;
  @Input() isInsideZoomPanel = false;
  @Input() pixelSize = 1;
  @Input() size?: Dimensions;
  @ViewChild('container') private containerRef!: ElementRef;

  translate!: string;
  layerOpacity$!: Observable<number>;
  shapeStores$!: Observable<Array<Store<ShapeState2D>>>;
  store = inject(AbstractSensorContentStore2D);
  scale$!: Observable<number>;
  missingShapeMarkerTransforms$!: Observable<string[]>;
  preserveAspectRatio!: string;

  private mouseCoordinateService = inject(MouseCoordinateService);
  private mouseEventService = inject(MouseEventService);
  private panningService = inject(PanningService);
  private shellConnector = inject(shellConnectorToken);
  private vectorRendererCssPropertyService = inject(
    VectorRendererCssPropertyService,
  );
  private vectorRendererCursorCssPropertyService = inject(
    VectorRendererCursorCssPropertyService,
  );

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.scale$ = this.buildScale$();
    this.shapeStores$ = this.mapOrderItemIndexesToStores$();
    this.layerOpacity$ = this.store('layerSettings').$.pipe(
      map((layerSettings) =>
        layerSettings.hideOccludedShapes ? layerSettings.opacity / 100 : 1,
      ),
    );
    this.missingShapeMarkerTransforms$ =
      this.buildMissingShapeMarkerTransforms$();
    this.vectorRendererCssPropertyService.manageCssProperties(this);
    this.vectorRendererCursorCssPropertyService.manageCssProperties(this);
    this.store('pixelSize').set(this.pixelSize);
    const halfPixelUnit = this.store.state().pixelSize / 2;
    this.translate = `translate(${halfPixelUnit}, ${halfPixelUnit})`;
    this.preserveAspectRatio = isOrthographicVideoOutput(
      this.store.state().output,
    )
      ? 'xMinYMin meet'
      : 'xMidYMid meet';
  }

  ngAfterViewInit() {
    if (this.isInsideZoomPanel) {
      return;
    }

    this.subscribeTo(this.getInput$('size').pipe(filter(isDefined)), (size) => {
      this.mouseCoordinateService.updateScale(size);
    });

    if (!this.isOverlay) {
      this.panningService.startListening(this.elementRef.nativeElement);

      const foreignObject: Element = this.containerRef.nativeElement;
      assert(
        foreignObject.parentElement,
        'The foreignObject always has the svg as parent',
      );
      this.mouseCoordinateService.registerSvgElement({
        foreignObject,
        svg: foreignObject.parentElement,
      });

      this.subscribeTo(
        this.mouseCoordinateService.updateOnMouseMove$(this.destruction$),
      );

      this.subscribeTo(
        this.mouseEventService.getEvent$('container', 'click'),
        () => {
          this.shellConnector.handleCanvasClick();
        },
      );
    }
  }

  handleMouseEvent(event: MouseEvent) {
    this.mouseEventService.handle({ event, target: 'container' });
  }

  private buildScale$() {
    return this.isInsideZoomPanel
      ? this.store('zoomPanelViewBox').$.pipe(
          map((zoomPanelViewBox) =>
            // The zoom panel has a fixed size of 400x400 pixels
            this.mouseCoordinateService.getScaleFromAspectRatio(
              { width: 400, height: 400 },
              zoomPanelViewBox,
            ),
          ),
        )
      : this.store('scale').$;
  }

  private mapOrderItemIndexesToStores$(): Observable<
    Array<Store<ShapeState2D>>
  > {
    const store: Store<ShapeGroupOrderState> = this.isOverlay
      ? this.store('vectorOverlay')
      : this.store;

    if (!this.store.state().project.use_annotation_store) {
      return this.mapIndexesToStoresObjectless$(store);
    }

    return combineLatest([
      this.getShapeAndGroupOrder$(store),
      this.store('selection').$,
      this.store('trainingShapes').$.pipe(
        map((trainingShapes) => (this.isOverlay ? {} : trainingShapes)),
      ),
      this.store('shapes').$,
    ]).pipe(
      map(([orderItems, selection, trainingShapes]) => [
        ...this.getItemsOrderedBySelection(orderItems, selection),
        ...this.mapToTrainingOrderItem(trainingShapes),
      ]),
      this.mapAndCacheOrderItems(store),
    );
  }

  /** @deprecated used for old projects without objects */
  private mapIndexesToStoresObjectless$(
    store: Store<ShapeGroupOrderState>,
  ): Observable<Array<Store<ShapeState2D>>> {
    const groupOrderState$ = store.$.pipe(
      map((state) => pick(state, 'groups', 'shapeAndGroupOrder')),
      distinctUntilChanged(),
    );

    return combineLatest([
      this.store('trainingShapes').$.pipe(
        map((trainingShapes) => (this.isOverlay ? {} : trainingShapes)),
      ),
      this.store('shapes').$,
      this.store('shapeFilters').$,
      groupOrderState$,
      this.store('selection').$,
    ]).pipe(
      map(([trainingShapes, shapes, filters, groupOrderState, selection]) => {
        const { shapeAndGroupOrder, groups } = groupOrderState;
        const orderItems = expandShapeGroups(shapeAndGroupOrder, groups).filter(
          (i) => applyFilters(shapes[i.index], filters),
        );
        return [
          ...this.getItemsOrderedBySelection(orderItems, selection),
          ...this.mapToTrainingOrderItem(trainingShapes),
        ];
      }),
      this.mapAndCacheOrderItems(store),
    );
  }

  private mapAndCacheOrderItems(store: Store<ShapeGroupOrderState>) {
    return mapAndCacheArrayElements<ShapeOrGroupOrderItem, Store<ShapeState2D>>(
      (orderItem) => this.buildCacheKey(orderItem),
      (orderItem) =>
        this.isTrainingOrderItem(orderItem)
          ? this.store('trainingShapes')(orderItem.index)
          : store('shapes')(orderItem.index),
    );
  }

  private buildCacheKey(item: ShapeOrGroupOrderItem) {
    return this.isTrainingOrderItem(item)
      ? item.cachingKey
      : item.index.toString();
  }

  private getShapeAndGroupOrder$(store: Store<ShapeGroupOrderState>) {
    return combineLatest([
      store.$.pipe(
        map((state) => state.shapeAndGroupOrder),
        distinctUntilChanged(),
      ),
      this.store('shapeTransientInfo').$,
    ]).pipe(
      map(([shapeAndGroupOrder, shapeTransientInfo]) =>
        shapeAndGroupOrder.filter(
          (item) => !shapeTransientInfo[item.index]?.isFilteredOut,
        ),
      ),
    );
  }

  private mapToTrainingOrderItem(
    shapes: StrictRecord<number, ShapeState2D>,
  ): Array<ShapeOrGroupOrderItem & { cachingKey: string }> {
    return keys(shapes).map((index) => ({
      type: 'shape',
      cachingKey: `training-${index}`,
      index: Number(index),
    }));
  }

  private isTrainingOrderItem(
    shape: any,
  ): shape is ShapeOrGroupOrderItem & { cachingKey: string } {
    return isDefined(shape.cachingKey);
  }

  private buildMissingShapeMarkerTransforms$() {
    return combineLatest([
      this.store('vectorOverlay')('shapeFeedbackMarkers').$,
      this.scale$,
    ]).pipe(
      map(([shapeFeedbackMarkers, scale]) =>
        shapeFeedbackMarkers.map((point) => {
          // the height and width of the SVG is 24pixels when scale is one
          const svgPadding = MISSING_SHAPE_MARKER_SVG_SIZE / scale;
          const translate = subtractPoints(point, [svgPadding / 2, svgPadding]);
          return `translate(${translate}) scale(${1 / scale})`;
        }),
      ),
    );
  }

  private getItemsOrderedBySelection(
    orderItems: ShapeOrGroupOrderItem[],
    selection: Selection,
  ) {
    if (!this.store.state().layerSettings.swapLayerOnSelect) {
      return orderItems;
    }

    return this.moveFocusedShapeToLast(orderItems, selection);
  }

  private moveFocusedShapeToLast(
    orderItems: ShapeOrGroupOrderItem[],
    selection: Selection,
  ) {
    const selectedShapesIndexes = Object.keys(selection);
    if (selectedShapesIndexes.length === 1 && orderItems.length) {
      const selectedShapeIndex = orderItems.findIndex(
        (item) => item.index === +selectedShapesIndexes[0],
      );
      if (selectedShapeIndex >= 0) {
        const [selected] = orderItems.splice(selectedShapeIndex, 1);
        orderItems.push(selected);
      }
    }

    return orderItems;
  }
}
